import { useMemo } from "react";
import PropTypes from "prop-types";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { ChevronDown, FolderInput } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { ToolbarButton } from "@/components/ui/toolbar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// The folder icon is an alternate trigger for the category list. Unlike the
// template menu this is a persisted selection: the current category is
// reflected back as the combobox value.
function CategoryMenu({ categories = [], value, onChange }) {
  const items = useMemo(
    () =>
      categories
        .filter(c => !c.hidden)
        .map(c => ({ value: c.key, label: c.text })),
    [categories]
  );

  // base-ui matches the selection by reference, so the scalar value has to be
  // resolved back to the item object it came from.
  const selectedItem = items.find(it => it.value === value) ?? null;
  const categoryName = selectedItem?.label;

  return (
    <Combobox
      items={items}
      value={selectedItem}
      onValueChange={next => onChange(next?.value ?? null)}
      itemToStringLabel={item => item?.label ?? ""}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <ToolbarButton
              render={
                <ComboboxPrimitive.Trigger
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size={selectedItem ? "sm" : "icon-sm"}
                      aria-label={
                        selectedItem
                          ? `Category: ${categoryName}`
                          : "Set category"
                      }
                      aria-pressed={selectedItem != null}
                      className="tw:aria-pressed:bg-muted tw:aria-pressed:text-foreground tw:dark:aria-pressed:bg-[color-mix(in_oklch,var(--color-highlight)_15%,transparent)] tw:dark:aria-pressed:text-[var(--color-highlight)]"
                      // Pressing the control must not collapse the editor
                      // selection.
                      onMouseDown={event => event.preventDefault()}
                    />
                  }
                />
              }
            />
          }
        >
          <FolderInput />
          {selectedItem && (
            <>
              <span className="tw:max-w-32 tw:truncate">{categoryName}</span>
              <ChevronDown />
            </>
          )}
        </TooltipTrigger>
        <TooltipContent>
          {selectedItem ? `Category: ${categoryName}` : "Set category"}
        </TooltipContent>
      </Tooltip>
      <ComboboxContent className="tw:min-w-52">
        <ComboboxInput
          showTrigger={false}
          showClear={value != null}
          placeholder="Select a Category"
        />
        <ComboboxEmpty>No categories</ComboboxEmpty>
        <ComboboxList>
          {item => (
            <ComboboxItem key={item.value} value={item}>
              {item.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

CategoryMenu.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

export default CategoryMenu;
